/**
 * ReferralScreen Component
 * Displays referral program: stats, earnings history, referral tree (3 levels)
 * With pagination (10 per page), search, and sponsor display
 */

import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';
import { useUserContext } from '../contexts';
import { withTimeout } from '../utils/timeout';

export function ReferralScreen() {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState<any>(null);
  const [tree, setTree] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [sponsor, setSponsor] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<'tree' | 'earnings'>('tree');
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Pagination state
  const [page, setPage] = useState(1);
  const [_totalPages, setTotalPages] = useState(1);
  const [_pagedReferrals, setPagedReferrals] = useState<any[]>([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (user) { loadData(); } else { setIsLoading(false); }
  }, [user]);

  useEffect(() => {
    if (user && activeTab === 'tree') { loadPagedReferrals(); }
  }, [page, user, activeTab]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [linkRes, statsRes, treeRes, earningsRes, sponsorRes] = await withTimeout(
        Promise.all([
          api.getReferralLink(),
          api.getReferralStats(),
          api.getReferralTree(),
          api.getReferralEarnings(50),
          api.getReferralSponsor(),
        ]),
        15000
      );
      setReferralLink(linkRes.data.referralLink || '');
      setStats(statsRes.data.stats);
      setTree(treeRes.data.tree || []);
      setEarnings(earningsRes.data.earnings || []);
      setSponsor(sponsorRes.data.sponsor || null);
    } catch (error: any) {
      setError(error.response?.data?.error?.message || error.message || 'Failed to load');
    } finally { setIsLoading(false); }
  };

  const loadPagedReferrals = async () => {
    try {
      const res = await api.getReferralList(page, 10);
      setPagedReferrals(res.data.referrals || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error('Failed to load paged referrals:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setIsSearching(true);
    try {
      const res = await api.searchReferrals(searchQuery.trim());
      setSearchResults(res.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally { setIsSearching(false); }
  };

  const copyToClipboard = async () => {
    try { await navigator.clipboard.writeText(referralLink); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); } catch {}
  };

  const shareLink = () => {
    if (navigator.share) { navigator.share({ title: 'WinTrade', text: t('referral.step_2'), url: referralLink }); } else { copyToClipboard(); }
  };

  const toggleNode = (id: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const getEarningTypeLabel = (type: string) => {
    switch (type) {
      case 'registration_bonus': return `👤 ${t('referral.earning_registration')}`;
      case 'trade_bonus': return `📈 ${t('referral.earning_trade')}`;
      case 'miner_purchase_bonus': return `⛏️ ${t('referral.earning_miner')}`;
      case 'ai_trader_package_bonus': return '🤖 AI Trader Bot';
      case 'ai_trader_reward_bonus': return '🤖 AI Trader Reward';
      default: return `💰 ${t('referral.earning_bonus')}`;
    }
  };

  const renderTreeNode = (node: any, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const levelColors = ['#3b82f6', '#f59e0b', '#ef4444'];
    const color = levelColors[depth] || '#6b7280';

    return (
      <div key={node.id} style={{ marginLeft: depth * 16 }}>
        <div
          onClick={() => hasChildren && toggleNode(node.id)}
          style={{
            background: 'rgba(10, 22, 40, 0.6)',
            borderRadius: '10px',
            padding: '10px 12px',
            marginBottom: '6px',
            border: `1px solid ${color}33`,
            cursor: hasChildren ? 'pointer' : 'default',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {hasChildren && <span style={{ fontSize: '10px', color }}>{isExpanded ? '▼' : '▶'}</span>}
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0,
              }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>
                  {node.username ? `@${node.username}` : node.firstName}
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                  L{node.level} • {new Date(node.joinedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color }}>L{node.level}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                {(node.balance || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} W • {(node.usdtBalance || 0).toFixed(1)}$ • {node.tradesCount || 0}T • {node.minersCount || 0}M
              </div>
            </div>
          </div>
        </div>
        {isExpanded && hasChildren && node.children.map((child: any) => renderTreeNode(child, depth + 1))}
      </div>
    );
  };

  if (isLoading) {
    return (<div style={{ minHeight: '100vh', backgroundColor: '#0a0e1a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{t('common.loading')}</div>);
  }

  if (!user || error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0e1a', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{error || t('common.error')}</div>
        <button onClick={loadData} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>🔄 {t('common.retry')}</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0a0e1a', color: '#fff', paddingBottom: '80px', overflowY: 'auto', maxHeight: '100vh' }}>
      <div style={{ padding: '20px 16px 16px', background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)', textAlign: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>{t('referral.title')}</h1>
      </div>

      {/* Sponsor Section */}
      {sponsor && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ background: 'linear-gradient(135deg, #065f46 0%, #059669 100%)', borderRadius: '12px', padding: '12px 14px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>👑 {t('referral.your_sponsor') || 'Ваш спонсор'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>👤</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>{sponsor.username ? `@${sponsor.username}` : sponsor.firstName}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>Level {sponsor.level}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Referral Link */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: '14px', padding: '16px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginBottom: '8px' }}>{t('referral.your_link')}</div>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', marginBottom: '12px', wordBreak: 'break-all', fontSize: '12px' }}>
            {referralLink || '...'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <button onClick={copyToClipboard} style={{ padding: '10px', background: copySuccess ? '#10b981' : 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              {copySuccess ? `✓ ${t('referral.link_copied')}` : t('referral.copy_link')}
            </button>
            <button onClick={shareLink} style={{ padding: '10px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              {t('referral.share_link')}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '8px' }}>
          <div style={{ background: 'linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)', borderRadius: '10px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{t('referral.total_referrals')}</div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>{stats?.totalReferrals || 0}</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)', borderRadius: '10px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{t('referral.total_earned')}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#4caf50' }}>{stats?.totalEarned?.toLocaleString() || 0}</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px' }}>
          {[
            { key: 'earned_from_registrations', val: stats?.totalEarnedFromRegistrations, icon: '👤' },
            { key: 'earned_from_trades', val: stats?.totalEarnedFromTrades, icon: '📈' },
            { key: 'earned_from_miners', val: stats?.totalEarnedFromMiners, icon: '⛏️' },
          ].map(({ key, val, icon }) => (
            <div key={key} style={{ background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', padding: '8px', border: '1px solid rgba(16, 185, 129, 0.2)', textAlign: 'center' }}>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>{icon} {t(`referral.${key}`)}</div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#10b981' }}>+{val?.toLocaleString() || 0}</div>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div style={{ padding: '0 16px 12px' }}>
        <button onClick={() => setShowInfo(!showInfo)} style={{ width: '100%', padding: '10px', background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          {showInfo ? '▼' : '▶'} {t('referral.how_it_works')}
        </button>
        {showInfo && (
          <div style={{ marginTop: '8px', background: 'rgba(10, 22, 40, 0.6)', borderRadius: '10px', padding: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', fontSize: '12px' }}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>{t('referral.level_1_info')}</div>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>{t('referral.level_2_info')}</div>
            <div style={{ fontWeight: 600 }}>{t('referral.level_3_info')}</div>
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: '0 16px 8px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('referral.search_placeholder') || 'Поиск по имени...'}
            style={{ flex: 1, padding: '10px 12px', background: 'rgba(10, 22, 40, 0.6)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }}
          />
          <button onClick={handleSearch} style={{ padding: '10px 14px', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            🔍
          </button>
        </div>
        {searchResults !== null && (
          <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                {isSearching ? '...' : `${searchResults.length} ${t('referral.results_found') || 'найдено'}`}
              </span>
              <button onClick={() => { setSearchResults(null); setSearchQuery(''); }} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '12px', cursor: 'pointer' }}>✕ {t('common.cancel') || 'Закрыть'}</button>
            </div>
            {searchResults.map((r) => (
              <div key={r.id} style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '8px', padding: '10px 12px', marginBottom: '4px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{r.username ? `@${r.username}` : r.firstName}</div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                  {new Date(r.joinedAt).toLocaleDateString()} • {Number(r.balance || 0).toLocaleString()} WiTon
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs: Tree / Earnings */}
      <div style={{ padding: '0 16px 8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
          <button onClick={() => setActiveTab('tree')}
            style={{ padding: '10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff',
              background: activeTab === 'tree' ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : 'rgba(255,255,255,0.1)' }}>
            🌳 {t('referral.structure')} ({stats?.totalReferrals || 0})
          </button>
          <button onClick={() => setActiveTab('earnings')}
            style={{ padding: '10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#fff',
              background: activeTab === 'earnings' ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : 'rgba(255,255,255,0.1)' }}>
            💰 {t('referral.earnings')} ({earnings.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '0 16px' }}>
        {activeTab === 'tree' ? (
          <>
            {tree.length === 0 ? (
              <div style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '12px', padding: '32px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                {t('referral.no_referrals')}
              </div>
            ) : (
              <>
                <div>{tree.slice(0, page * 10).map((node) => renderTreeNode(node, 0))}</div>
                {/* Pagination */}
                {tree.length > page * 10 && (
                  <button onClick={() => setPage(p => p + 1)}
                    style={{ width: '100%', padding: '12px', marginTop: '8px', background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '8px', color: '#60a5fa', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                    {t('referral.show_more') || 'Показать ещё'} ({tree.length - page * 10})
                  </button>
                )}
              </>
            )}
          </>
        ) : (
          earnings.length === 0 ? (
            <div style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '12px', padding: '32px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
              {t('referral.no_referrals')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {earnings.map((e) => (
                <div key={e.id} style={{ background: 'rgba(10, 22, 40, 0.6)', borderRadius: '10px', padding: '10px 12px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>
                        {getEarningTypeLabel(e.type)} • {e.referralUsername}
                      </div>
                      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                        L{e.level} • {new Date(e.createdAt).toLocaleDateString()} {new Date(e.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      {/* Chain display for L2/L3 bonuses */}
                      {e.chain && e.chain.length > 0 && (
                        <div style={{ fontSize: '10px', color: '#60a5fa', marginTop: '2px' }}>
                          {t('referral.l1_sponsor') || 'L1'}: {e.chain[0]?.username || e.chain[0]?.firstName || '—'}
                          {e.chain.length > 1 && ` → ${e.chain.map((c: any) => c.username || c.firstName).join(' → ')}`}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#10b981' }}>
                      {e.currency === 'USDT' ? `+$${Number(e.amount).toFixed(2)}` : `+${e.amount.toLocaleString()}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
